import React, { useEffect, useState } from 'react'
import { axiosGet } from '../../../util/api'
import { Switch } from '../../../components/ui/switch'
import { Layout } from '../../../components/Layout'
import { Chart } from '../../../components/chart/chart'
import { ClassValue } from 'classnames/types'
import cx from 'classnames'
import { RecoilValueReadOnly, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { capitalCase } from 'change-case'
import {
    activeAtom,
    AggregatesI,
    aggregates30D,
    aggregates7D,
    datasetSelector,
    errorRateSelector,
    rawCounts,
    totalsSelector,
    UpdateStatuses,
    useLastDayAtom,
} from './admin_updates_state'
import { RecentlyFailedUpdatesTable } from './RecentlyFailedUpdatesTable'
import { MostFailedByProfileTable } from './MostFailedByProfileTable'

export const AdminUpdates = () => {
    const active = useRecoilValue(activeAtom)
    const [loading, setLoading] = useState(false)
    const totals = useRecoilValue(totalsSelector)
    const errorRate = useRecoilValue(errorRateSelector)
    const setRawData = useSetRecoilState(rawCounts)
    const dataset = useRecoilValue(datasetSelector)
    const [showLastDay, setShowLastDay] = useRecoilState(useLastDayAtom)

    useEffect(() => {
        setLoading(true)
        axiosGet('admin/updates')
            .then((r) => {
                const res = r.data as { [key: number]: UpdateStatuses<number> }
                setRawData(res)

                setLoading(false)
            })
            .catch(() => {
                setLoading(false)
            })
    }, [])

    return (
        <Layout primary="Updates">
            <Switch
                label="Show Today"
                checked={showLastDay}
                onChange={() => setShowLastDay((p) => !p)}
            />
            <div className="flex gap-x-8">
                <div className="w-full" style={{ maxWidth: '50%' }}>
                    <Chart
                        loading={loading}
                        compare={false}
                        title={`Update Counts: ${active}`}
                        data={dataset}
                        className=""
                        formatter={(x) => x}
                        current={totals[active] as number}
                        previous={0}
                    />
                    <div className="flex w-full justify-between border rounded-b-md">
                        <StatusButton status="success" />
                        <StatusButton status="failure" />
                        <StatusButton status="in progress" />
                        <StatusButton status="request finished" />
                        <StatusButton status="created" last />
                    </div>
                </div>
                <div className="w-full" style={{ maxWidth: '50%' }}>
                    <Chart
                        loading={loading}
                        compare={false}
                        title="Error Rate"
                        data={errorRate}
                        className=""
                        formatter={(x) => x + '%'}
                        current={totals['error rate']}
                        previous={0}
                    />
                </div>
            </div>
            <div className="flex gap-x-8 mt-8">
                <div className="w-full" style={{ maxWidth: '50%' }}>
                    <Aggregates title="7 Day" selector={aggregates7D} />
                </div>
                <div className="w-full" style={{ maxWidth: '50%' }}>
                    <Aggregates title="30 Day" selector={aggregates30D} />
                </div>
            </div>

            <div className="flex gap-x-8 mt-8">
                <div className="w-full" style={{ maxWidth: '50%' }}>
                    <RecentlyFailedUpdatesTable />
                </div>
                <div className="w-full" style={{ maxWidth: '50%' }}>
                    <MostFailedByProfileTable />
                </div>
            </div>
        </Layout>
    )
}

interface AggregateProps {
    title: string
    selector: RecoilValueReadOnly<AggregatesI>
}

const Aggregates = ({ title, selector }: AggregateProps) => {
    const aggregates = useRecoilValue(selector)

    return (
        <div className=" border rounded-md bg-white">
            <div className="border-b px-3 py-2 bg-gray-50">{title}</div>
            <div className="px-3 py-2 flex w-full justify-content-between">
                <Metric title="Success Rate" value={aggregates.success_rate} suffix="%" />
                <Metric title="Error Rate" value={aggregates.error_rate} suffix="%" />
                <Metric title="Total" value={aggregates.total} />
                <Metric title="Errors" value={aggregates.errors} />
            </div>
        </div>
    )
}

interface MetricProps {
    title: string
    value: number
    suffix?: string
}

const Metric = ({ title, value, suffix }: MetricProps) => {
    return (
        <div className="flex flex-col">
            <div>{title}</div>
            <div className="text-xl font-semibold">
                {value}
                {suffix}
            </div>
        </div>
    )
}

interface StatusButtonProps {
    status: keyof UpdateStatuses<any>
    last?: boolean
}

const StatusButton = ({ status, last = false }: StatusButtonProps) => {
    const [active, setActive] = useRecoilState(activeAtom)
    const totals = useRecoilValue(totalsSelector)
    const isActive = active == status

    return (
        <Button
            className={cx({ 'border-r': !last, 'bg-white': !isActive, 'bg-gray-50': isActive })}
            onClick={() => setActive(status)}
        >
            {capitalCase(status)} ({totals[status]})
        </Button>
    )
}

interface ButtonProps {
    children: any
    onClick: () => any
    className?: ClassValue
    active?: boolean
}

const Button = ({ children, className, onClick, active = false }: ButtonProps) => {
    return (
        <div
            className={cx(
                'p-3 whitespace-nowrap w-full flex items-center justify-center cursor-pointer hover:bg-gray-50',
                className,
            )}
            onClick={() => onClick()}
        >
            {children}
        </div>
    )
}
